import { ExtendedDatePicker } from '@/helpers/dateHelper';
import { portalAPI } from '@/helpers/environmentHelper';
import {
    buildPortfolioStatementData,
    exportPortfolioStatementPDF,
    getStatementNumberFormatOptions,
    isPortfolioAccountType,
    statementDateFormatOptions
} from '@/helpers/portfolioStatementHelper';
import { logToServer } from '@/services/LogService';
import { ErrorMsg } from '@/types/common';
import useAbortController from '@/utils/hooks/useAbortController';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import { useStatementFilter } from '@/utils/hooks/useStatementFilter';
import { useSso } from '@/utils/providers/SSOProvider';
import axios from 'axios';
import {
    endOfMonth,
    endOfWeek,
    format,
    isAfter,
    isSameDay,
    isToday,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subMonths,
    subWeeks
} from 'date-fns';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDateFormatter, useLocale } from 'react-aria';
import { default as DatePicker } from 'react-datepicker';
import { Button } from '../common/Button';
import Divider from '../common/Divider';
import TabFilters from '../common/TabFilters';
import ErrorMessage from '../inputs/ErrorMessage';
import PortalInput from '../inputs/PortalInput';
import Select from '../inputs/Select';
import DatePickerInput from './HistoricSearchModal/DatePickerInput';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

const tzOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
};

type StatementPeriod = 'custom' | 'daily' | 'weekToDate' | 'lastWeek' | 'monthToDate' | 'lastMonth';
export type StatementPeriodOption = { label: string; value: StatementPeriod };

const periodOptions: StatementPeriodOption[] = [
    { label: 'Custom', value: 'custom' },
    { label: 'Daily', value: 'daily' },
    { label: 'Week to Date', value: 'weekToDate' },
    { label: 'Last Week', value: 'lastWeek' },
    { label: 'Month to Date', value: 'monthToDate' },
    { label: 'Last Month', value: 'lastMonth' }
];

type StatementFormat = 'pdf' | 'xls';
export type StatementFormatOption = { label: string; value: StatementFormat };

const formatOptions: StatementFormatOption[] = [
    { label: 'PDF Document', value: 'pdf' },
    { label: 'Excel Document', value: 'xls' }
];

type StatementType = 'general' | 'gb10';

type PortfolioAccountOption = { label: string; value: string; accountName: string; accountType: string };

export type FileProgressType = {
    id: string;
    state: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    fileType: 'pdf' | 'xls';
    startDate?: Date | null;
    endDate?: Date | null;
    entityId?: number | null;
};

export const defaultStatement: FileProgressType = {
    id: '',
    state: 'SCHEDULED',
    fileType: 'pdf',
    startDate: null,
    endDate: null,
    entityId: null
};

export const NYDtFormat = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    ...tzOptions
});

const confirmText =
    'The statement is in the midst of being generated. Are you sure you want to close this window and terminate the process?';

const StatementModal = (props: ModalProps) => {
    const { opened, handlers } = props;

    const lang = useLanguage();
    const { locale } = useLocale();
    const { on401Error } = use401Error();
    const { foundEntity, jwt, summaryApi, fetchSummary } = useSso();
    const statementDateFormatter = useDateFormatter(statementDateFormatOptions);

    const startDateRef = useRef<ExtendedDatePicker | null>(null);
    const endDateRef = useRef<ExtendedDatePicker | null>(null);
    const gb10DateRef = useRef<ExtendedDatePicker | null>(null);
    // Incremented on open/close so in-flight portfolio statement requests from a previous session are ignored
    const gb10RequestIdRef = useRef(0);

    const [gb10Date, setGb10Date] = useState<Date>(new Date());
    const [isGb10Loading, setIsGb10Loading] = useState<boolean>(false);
    const [statementType, setStatementType] = useState<StatementType>('general');
    const [selectedGb10Account, setSelectedGb10Account] = useState<PortfolioAccountOption | null>(null);

    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [error, setError] = useState<ErrorMsg>({ message: '' });
    const [selectedPeriod, setSelectedPeriod] = useState<StatementPeriodOption>(periodOptions[0]);
    const [selectedFormat, setSelectedFormat] = useState<StatementFormatOption>(formatOptions[0]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [xlsContent, setXlsContent] = useState<Uint8Array | null>(null);
    const [pdfContent, setPdfContent] = useState<any | null>(null);
    const [statementDetails, setStatementDetails] = useState<any>(defaultStatement);
    const [statementInterval, setStatementInterval] = useState<NodeJS.Timer | null>(null);
    const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);
    const [showLongWaitMessage, setShowLongWaitMessage] = useState<boolean>(false);

    const { abortController, setAbortController, onClose } = useAbortController({
        confirmText,
        isLoading,
        handlers,
        callback: () => {
            if (statementInterval) clearInterval(statementInterval);
        }
    });

    const { disabledDateInputs, defaultDatePickerProps, lastCompletedTradeDate, generateStatement } =
        useStatementFilter({
            isLoading,
            setIsLoading,
            setXlsContent,
            setPdfContent,
            setStatementDetails,
            selectedPeriod,
            startDate,
            endDate
        });

    const resetGeneratedStatements = useCallback(() => {
        setError({ message: '' });
        setIsLoading(false);
        setXlsContent(null);
        setPdfContent(null);
        setStatementDetails(defaultStatement);
    }, [setXlsContent, setStatementDetails]);

    const handleApiError = useCallback(
        (error) => {
            setError({ message: error?.message || 'An error occurred while processing your request.' });
            setIsLoading(false);
        },
        [setIsLoading]
    );

    useEffect(() => {
        if (selectedPeriod.value === 'daily') setEndDate(startDate);
    }, [selectedPeriod, startDate]);

    useEffect(() => {
        if (selectedPeriod.value === 'weekToDate') {
            setStartDate(startOfWeek(lastCompletedTradeDate, { weekStartsOn: 1 }));
            setEndDate(lastCompletedTradeDate);
        }
        if (selectedPeriod.value === 'lastWeek') {
            setStartDate(subWeeks(startOfWeek(lastCompletedTradeDate, { weekStartsOn: 1 }), 1));
            setEndDate(subWeeks(endOfWeek(lastCompletedTradeDate, { weekStartsOn: 1 }), 1));
        }
        if (selectedPeriod.value === 'monthToDate') {
            setStartDate(startOfMonth(lastCompletedTradeDate));
            setEndDate(lastCompletedTradeDate);
        }
        if (selectedPeriod.value === 'lastMonth') {
            setStartDate(startOfMonth(subMonths(lastCompletedTradeDate, 1)));
            setEndDate(endOfMonth(subMonths(lastCompletedTradeDate, 1)));
        }
    }, [selectedPeriod, lastCompletedTradeDate]);

    useEffect(() => {
        // Set Start and End Date to minTime when initialized
        setStartDate(lastCompletedTradeDate);
        setEndDate(lastCompletedTradeDate);
    }, [lastCompletedTradeDate]);

    useDidUpdate(resetGeneratedStatements, [foundEntity]);

    const portfolioAccountOptions = useMemo<PortfolioAccountOption[]>(() => {
        const accountBased = summaryApi.data?.balancesSummary?.balances?.accountBased ?? [];
        return accountBased
            .filter((account) => isPortfolioAccountType(account.accountType))
            .map((account) => ({
                label: account.accountName,
                value: `${account.accountType}-${account.accountName}`,
                accountName: account.accountName,
                accountType: account.accountType
            }));
    }, [summaryApi.data]);

    const hasPortfolioAccounts = portfolioAccountOptions.length > 0;

    // Only today (current balances) or dates up to the last completed trade date (historic balances) are valid
    const gb10DateResolution = useMemo(() => {
        const isCurrent = isToday(gb10Date);
        const shouldFallback = !isCurrent && isAfter(startOfDay(gb10Date), startOfDay(lastCompletedTradeDate));
        return { isCurrent, shouldFallback, effectiveDate: shouldFallback ? lastCompletedTradeDate : gb10Date };
    }, [gb10Date, lastCompletedTradeDate]);

    const statementTypeOptions = useMemo(
        () => [
            { label: lang.portal.generalStatements, value: 'general' },
            ...(hasPortfolioAccounts ? [{ label: lang.portal.portfolioStatements, value: 'gb10' }] : [])
        ],
        [lang, hasPortfolioAccounts]
    );

    useEffect(() => {
        if (!hasPortfolioAccounts && statementType === 'gb10') setStatementType('general');
    }, [hasPortfolioAccounts, statementType]);

    useEffect(() => {
        setSelectedGb10Account((prev) => {
            const stillExists = prev && portfolioAccountOptions.find((option) => option.value === prev.value);
            return stillExists || portfolioAccountOptions[0] || null;
        });
    }, [portfolioAccountOptions]);

    const generateGb10Statement = useCallback(async () => {
        if (!foundEntity || !selectedGb10Account) return;

        const requestId = ++gb10RequestIdRef.current;
        setError({ message: '' });
        setIsGb10Loading(true);

        try {
            const { isCurrent, effectiveDate } = gb10DateResolution;
            const tradeDate = format(effectiveDate, 'yyyy-MM-dd');
            const summary = await fetchSummary(isCurrent ? undefined : tradeDate);
            if (requestId !== gb10RequestIdRef.current) return;

            const balances = summary?.balancesSummary;
            const account = balances?.balances?.accountBased?.find(
                (item) =>
                    item.accountName === selectedGb10Account.accountName &&
                    item.accountType === selectedGb10Account.accountType
            );

            if (!balances || !account) {
                setError({ message: lang.portal.noBalancesFoundForAccountOnDate });
                return;
            }

            const statementData = buildPortfolioStatementData({
                balances,
                account,
                entityName: foundEntity.entityName || '',
                date: isCurrent ? new Date() : new Date(tradeDate),
                locale,
                dateFormatter: statementDateFormatter,
                numberFormatter: new Intl.NumberFormat(locale, getStatementNumberFormatOptions(balances.baseCurrency))
            });

            await exportPortfolioStatementPDF(statementData, tradeDate);
        } catch (error: any) {
            if (requestId !== gb10RequestIdRef.current) return;
            on401Error(error, () => {
                logToServer('error', `Unauthenticated access on statement modal. JWT may be invalid or expired.`);
            });
            logToServer(
                'error',
                `Entity [${foundEntity?.entityName}] encountered error while generating portfolio statement: ${
                    error?.message || error
                }`
            );
            setError({ message: error?.message || 'An error occurred while processing your request.' });
        } finally {
            if (requestId === gb10RequestIdRef.current) setIsGb10Loading(false);
        }
    }, [foundEntity, selectedGb10Account, gb10DateResolution, fetchSummary, locale, statementDateFormatter, lang]);

    const getStatusMessage = useCallback(() => {
        if (!statementDetails.id) return '';

        switch (statementDetails.state) {
            case 'SCHEDULED':
                return lang.portal.statementQueuedForGeneration;
            case 'IN_PROGRESS':
                return lang.portal.processingStatementData;
            case 'COMPLETED':
                return lang.portal.generatingComplete;
            default:
                return lang.portal.generatingStatement;
        }
    }, [statementDetails.state, statementDetails.id]);

    const checkStatementStatus = useCallback(
        async (interval: NodeJS.Timer, abortController) => {
            try {
                if (
                    foundEntity &&
                    statementDetails.entityId === foundEntity.entityId &&
                    statementDetails.startDate &&
                    statementDetails.endDate
                ) {
                    const statusRequest: any = await axios.post(
                        portalAPI('/api/statement/status'),
                        { statementId: statementDetails.id },
                        { headers: { Authorization: `Bearer ${jwt}` }, signal: abortController.signal }
                    );

                    if (statusRequest.data.state === 'FAILED') {
                        throw new Error(statusRequest.data.message);
                    }
                    setStatementDetails({
                        ...statementDetails,
                        id: statusRequest.data.id,
                        state: statusRequest.data.state
                    });
                }
            } catch (error: any) {
                on401Error(error, () => {
                    logToServer('error', `Unauthenticated access on statement modal. JWT may be invalid or expired.`);
                });
                logToServer(
                    'error',
                    `Entity [${foundEntity?.entityName}] encountered error while checking statement status: ${
                        error?.message || error
                    }`
                );
                handleApiError(error);
                clearInterval(interval);
            }
        },
        [statementDetails, foundEntity, jwt]
    );

    const downloadStatement = useCallback(
        async (abortController) => {
            try {
                // Due to the nature of intervals, some residual processes will be conducted even after statement is reset,
                // so as a temporary solution we should check if current statementDetails is still relevant before downloading.
                if (
                    foundEntity &&
                    statementDetails.entityId === foundEntity.entityId &&
                    statementDetails.startDate &&
                    statementDetails.endDate
                ) {
                    const downloadRequest: any = await axios.post(
                        portalAPI('/api/statement/download'),
                        { statementId: statementDetails.id },
                        {
                            headers: { Authorization: `Bearer ${jwt}` },
                            responseType: 'arraybuffer',
                            signal: abortController.signal
                        }
                    );
                    const { fileType, startDate, endDate } = statementDetails;
                    if (fileType === 'pdf') {
                        setPdfContent(downloadRequest.data as any as Uint8Array);
                    } else if (fileType === 'xls') {
                        setXlsContent(downloadRequest.data as any as Uint8Array);
                    }

                    const fileUrl = URL.createObjectURL(
                        new Blob([downloadRequest.data], {
                            type:
                                fileType === 'pdf'
                                    ? 'application/pdf'
                                    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        })
                    );

                    const link = document.createElement('a');
                    link.href = fileUrl;
                    const from = format(startDate, 'yyyy-MM-dd');
                    const to = format(endDate, 'yyyy-MM-dd');
                    if (isSameDay(startDate, endDate)) {
                        const fileName = `${foundEntity.entityName}_${from}.${fileType === 'pdf' ? 'pdf' : 'xlsx'}`;
                        link.setAttribute('download', fileName);
                    } else {
                        const fileName = `${foundEntity.entityName}_${from}_${to}.${
                            fileType === 'pdf' ? 'pdf' : 'xlsx'
                        }`;
                        link.setAttribute('download', fileName);
                    }
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(fileUrl);
                }
                setIsLoading(false);
            } catch (error: any) {
                on401Error(error, () => {
                    logToServer('error', `Unauthenticated access on statement modal. JWT may be invalid or expired.`);
                });
                logToServer(
                    'error',
                    `Entity [${foundEntity?.entityName}] encountered error while downloading statement: ${
                        error?.message || error
                    }`
                );
                handleApiError(error);
            }
        },
        [foundEntity, statementDetails, jwt]
    );

    useEffect(() => {
        if (statementDetails && statementDetails.id) {
            if (statementDetails.state !== 'COMPLETED') {
                const interval = setInterval(() => checkStatementStatus(interval, abortController), 2000);
                setStatementInterval(interval);
                return () => clearInterval(interval);
            } else {
                if (!pdfContent && !xlsContent) downloadStatement(abortController); // Invoke the downloadStatement function here
            }
        } else {
            // Reset loading state and clear interval if statements are reset elsewhere in the code
            if (statementInterval) clearInterval(statementInterval);
            setIsLoading(false);
        }
    }, [statementDetails]);

    useEffect(() => {
        if (isLoading && !generationStartTime) {
            setGenerationStartTime(Date.now());
            setShowLongWaitMessage(false);
        }
    }, [isLoading, generationStartTime]);

    useEffect(() => {
        if (!isLoading) {
            setGenerationStartTime(null);
            setShowLongWaitMessage(false);
        }
    }, [isLoading]);

    useEffect(() => {
        if (!isLoading || !generationStartTime) return;

        const timer = setTimeout(() => {
            const elapsedTime = Date.now() - generationStartTime;
            if (elapsedTime > 15000 && isLoading) {
                setShowLongWaitMessage(true);
            }
        }, 15000);

        return () => clearTimeout(timer);
    }, [isLoading, generationStartTime]);

    useEffect(() => {
        if (opened) {
            setAbortController(new AbortController());
            setStartDate(new Date(lastCompletedTradeDate || ''));
            setEndDate(new Date(lastCompletedTradeDate || ''));
            setSelectedPeriod(periodOptions[0]);
            setSelectedFormat(formatOptions[0]);
            setStatementType('general');
            setGb10Date(new Date());
            resetGeneratedStatements();
        } else abortController.abort();

        gb10RequestIdRef.current++;
        setIsGb10Loading(false);

        return () => abortController.abort();
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size="max-w-md">
            <ModalHeader>
                <ModalTitle>{lang.portal.generateAccountStatement}</ModalTitle>
                <ModalClose onClick={onClose} />
            </ModalHeader>
            <Fragment>
                <Divider neutral />
                {statementTypeOptions.length > 1 && (
                    <TabFilters.Main>
                        {statementTypeOptions.map((option) => (
                            <TabFilters.Filter
                                key={option.value}
                                filter={option}
                                selectedFilter={statementType}
                                setSelectedFilter={(value: StatementType) => {
                                    if (isLoading || isGb10Loading) return;
                                    setError({ message: '' });
                                    setStatementType(value);
                                }}
                            />
                        ))}
                    </TabFilters.Main>
                )}
            </Fragment>
            <div className="p-2 sm:p-4">
                {statementType === 'gb10' ? (
                    <div className="flex flex-col gap-4">
                        <div className="text-xs text-brand-neutral-400">
                            {lang.portal.selectTheDateAndAccountForPortfolioStatement}
                        </div>
                        <div className="flex flex-col gap-4 text-sm">
                            <PortalInput
                                label={lang.portal.account}
                                value={selectedGb10Account}
                                onChange={(value) => setSelectedGb10Account(value)}
                                disabled={isGb10Loading}>
                                <Select options={portfolioAccountOptions} />
                            </PortalInput>
                            <div className="w-full">
                                <DatePicker
                                    ref={gb10DateRef}
                                    selected={gb10Date}
                                    onChange={(date) => {
                                        if (date) {
                                            setGb10Date(date);
                                            gb10DateRef.current?.setOpen(false);
                                        }
                                    }}
                                    customInput={
                                        <DatePickerInput
                                            label={lang.portal.historicDate}
                                            labelStyle={2}
                                            parentRef={gb10DateRef}
                                            className="h-[38px] !rounded-md !w-full !bg-brand-background-dark"
                                            inline
                                        />
                                    }
                                    disabled={isGb10Loading}
                                    {...defaultDatePickerProps}
                                    maxDate={new Date()}
                                />
                            </div>
                            {gb10DateResolution.shouldFallback && (
                                <div className="text-xs text-yellow-400 bg-yellow-500/20 border border-yellow-500/40 rounded-md p-2">
                                    {lang.portal.gb10DateFallbackWarning.replace(
                                        '{value}',
                                        format(lastCompletedTradeDate, 'yyyy-MM-dd')
                                    )}
                                </div>
                            )}
                            <PortalInput label={lang.portal.format} value={formatOptions[0]} disabled>
                                <Select options={[formatOptions[0]]} />
                            </PortalInput>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <div className="text-xs text-brand-neutral-400">
                            {lang.portal.selectTheDateRangeToBeIncluded}
                        </div>
                        <div className="flex flex-col gap-4 text-sm">
                            <PortalInput
                                label={lang.common.dateRange}
                                value={selectedPeriod}
                                onChange={(value) => setSelectedPeriod(value)}
                                containerClassName="!w-fit">
                                <Select options={periodOptions} />
                            </PortalInput>
                            <div className="flex gap-3">
                                <div className="w-full">
                                    <DatePicker
                                        ref={startDateRef}
                                        selected={startDate}
                                        onChange={(date) => {
                                            if (date) {
                                                setStartDate(date);
                                                startDateRef.current?.setOpen(false);
                                            }
                                        }}
                                        customInput={
                                            <DatePickerInput
                                                label={lang.common.startDate}
                                                labelStyle={2}
                                                parentRef={startDateRef}
                                                className="h-[38px] !rounded-md !w-full !bg-brand-background-dark"
                                                inline
                                            />
                                        }
                                        disabled={disabledDateInputs.startDate}
                                        {...defaultDatePickerProps}
                                    />
                                </div>
                                <div className="w-full">
                                    <DatePicker
                                        ref={endDateRef}
                                        selected={endDate}
                                        onChange={(date) => {
                                            if (date) {
                                                setEndDate(date);
                                                endDateRef.current?.setOpen(false);
                                            }
                                        }}
                                        customInput={
                                            <DatePickerInput
                                                label={lang.common.endDate}
                                                labelStyle={2}
                                                parentRef={endDateRef}
                                                className="h-[38px] !rounded-md !w-full !bg-brand-background-dark"
                                                inline
                                            />
                                        }
                                        disabled={disabledDateInputs.endDate}
                                        {...defaultDatePickerProps}
                                    />
                                </div>
                            </div>
                            <PortalInput
                                label={lang.portal.format}
                                value={selectedFormat}
                                onChange={(value) => setSelectedFormat(value)}>
                                <Select options={formatOptions} />
                            </PortalInput>
                        </div>
                        <div className="text-xs text-brand-neutral-400">
                            {lang.portal.pleaseNoteThatTheGeneratedStatement}
                        </div>
                        {isLoading && showLongWaitMessage && (
                            <div className="flex flex-col gap-3 p-3 bg-brand-neutral-800 rounded-md border border-brand-neutral-700">
                                {/* <div className="text-sm text-brand-accent flex items-center gap-2">
                                <span className="inline-block w-2 h-2 bg-brand-accent rounded-full animate-pulse"></span>
                                {getStatusMessage()}
                            </div> */}
                                {showLongWaitMessage && (
                                    <div className="text-xs text-brand-neutral-300 border-t border-brand-neutral-700 pt-2">
                                        <p className="font-medium mb-1">{lang.portal.takingLongerThanExpected}</p>
                                        <p>{lang.portal.remainPatient}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <ErrorMessage error={error} />
            </div>
            <Divider neutral />
            <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                <button
                    type="button"
                    className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                    onClick={onClose}>
                    {lang.common.cancel}
                </button>
                <Button
                    type="button"
                    className="w-auto rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                    onClick={() => {
                        setError({ message: '' });
                        if (statementType === 'gb10') generateGb10Statement();
                        else generateStatement(selectedFormat.value);
                    }}
                    disabled={statementType === 'gb10' && !selectedGb10Account}
                    isLoading={statementType === 'gb10' ? isGb10Loading : isLoading}>
                    {lang.portal.generateStatement}
                </Button>
            </div>
        </Modal>
    );
};

export default StatementModal;
